/**
 * The Studio's screens under its home (pages/Home.tsx), lazily loaded: the
 * Studio pulls in React Flow and Yjs, and the list does not need them.
 *
 * /runs/new starts a run from a protocol version, an optional patient and the
 * conversation it was offered in; an agent links it. /runs/:id is the run
 * screen: a protocol run is a tricorder-session session that pins no agent and
 * holds the run's log, which the browser appends each accepted step to
 * (tricorder-protocol keeps no run). /sessions/:id is where the main app opens
 * a session it hands to this app (care-app RemoteAgentFace): a session that
 * pins no agent is a run (tricorder-session ADR-0009) and opens on the run
 * screen; any other opens on the library.
 */
import { lazy } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { ErrorWithRetry, Spinner, type AppRoute } from '@ai4nr/tricorder-ui'

import { sessionsApi } from '@/api/sessions'

const Studio = lazy(() => import('./pages/Studio'))
const Run = lazy(() => import('./pages/Run'))
const StartRun = lazy(() => import('./pages/StartRun'))

function SessionEntry() {
  const { id = '' } = useParams()
  const read = useQuery({ queryKey: ['session-entry', id], queryFn: () => sessionsApi.get(id) })
  if (read.isError) {
    return (
      <div className="flex h-dvh items-center justify-center p-6">
        <ErrorWithRetry error={read.error} onRetry={() => void read.refetch()} />
      </div>
    )
  }
  if (!read.data) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
      </div>
    )
  }
  return <Navigate to={read.data.agent ? '/' : `/runs/${id}`} replace />
}

export const routes: AppRoute[] = [
  // The authoring canvas — new protocol, or editing an existing version.
  { path: '/studio', element: <Studio /> },
  { path: '/studio/:protocolId/:version', element: <Studio /> },
  // Start a run, then replace the URL with the run's own.
  { path: '/runs/new', element: <StartRun /> },
  // One protocol run, walked step by step.
  { path: '/runs/:id', element: <Run /> },
  { path: '/sessions/:id', element: <SessionEntry /> },
]
